import { computed, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import type { FieldInitialSettings, FieldSchema, RawField } from '@FormModule';
import type { GenericField } from '@Types';
import { toCents } from '@Helpers';
import { useSessionStore } from '@Stores';
import { useForm } from '@FormModule';
import { PAYMENT_OPTIONS } from '../../../../constants';
import { PAYMENT_METHODS } from '../../../../enums';
import { UpdateCartTotalsService } from '../../../../services';
import useShoppingCartStore from '../../../../store';

export default function () {
  const { cartData, cartTotal, cashDrawerOptions, isDonation } = storeToRefs(useShoppingCartStore());
  const { shelterProfile } = storeToRefs(useSessionStore());

  const { removeHiddenFieldsFromCurrentSchemaModels, getDetailsSchema } = useForm();

  const fieldsSettings: FieldInitialSettings = {
    cash_drawer: {
      settings: {
        show: !!shelterProfile.value?.cash_drawer,
      },
      edit: {
        label: 'label',
        options: cashDrawerOptions.value,
      },
    },
    last_four_digits: {
      settings: {
        max: 9999,
        maxFractionDigits: null,
        minFractionDigits: null,
        placeholder: 'cart.label.lastFourPlaceholder',
        useGrouping: false,
        showButtons: false,
      },
    },
    payment_type: {
      edit: {
        options: [],
      },
    },
    split_payment: {
      model: {
        card_reader_amount: cartData.value?.split_payment_details?.card_reader_amount ?? 0,
        cart_total: Number(cartTotal.value),
        cash_amount: cartData.value?.split_payment_details?.cash_amount ?? 0,
        check_amount: cartData.value?.split_payment_details?.check_amount ?? 0,
        check_number: cartData.value?.split_payment_details?.check_number ?? '',
        credit_card_amount: cartData.value?.split_payment_details?.credit_card_amount ?? null,
        last_four_digits: cartData.value?.split_payment_details?.last_four_digits ?? '',
        name_on_card: cartData.value?.split_payment_details?.name_on_card ?? undefined,
        other_amount: cartData.value?.split_payment_details?.other_amount ?? 0,
        split_total: cartData.value?.split_payment_details?.split_total ?? 0,
        card_complete: false,
      },
      settings: {
        cartTotal: getAmountToSplit(),
        cc_fee: Number(cartData.value?.cc_fee) ?? 0,
        creditCardEnabled: !!shelterProfile.value?.stripe,
        show: false,
      },
    },
    stripe: {
      settings: {
        show: false,
      },
    },
    cash_amount_collected: {
      settings: {
        show: false,
        totalCharge: cartData.value?.total_charge ?? cartData.value?.total ?? 0,
      },
    },
    receipt_notes: {
      settings: {
        show: !isDonation.value,
      },
    },
  };

  const rawFields: RawField[] = [
    {
      label: 'cart.label.cashDrawer',
      name: 'cash_drawer',
      required: true,
      type: 'select',
      value: '',
    },
    {
      label: 'components.forms.labels.paymentOptions',
      name: 'payment_type',
      required: true,
      type: 'button_checkbox',
      value: '',
    },
    {
      label: '',
      name: 'stripe',
      required: false,
      type: 'stripe',
      value: '',
    },
    {
      label: '',
      name: 'split_payment',
      required: false,
      type: 'split_payment',
      value: '',
    },
    {
      label: 'cart.label.lastFour',
      name: 'last_four_digits',
      required: false,
      type: 'card_reader',
      value: '',
    },
    {
      label: 'cart.label.checkNumber',
      name: 'check_number',
      required: false,
      type: 'check_number',
      value: '',
    },
    {
      label: '',
      name: 'cash_amount_collected',
      required: false,
      type: 'cash_payment',
      value: null,
    },
    {
      label: 'cart.label.receiptNotes',
      name: 'receipt_notes',
      required: false,
      type: 'textarea',
      value: '',
    },
  ];

  const currentSchemaModels = ref<Set<GenericField>>(new Set());
  const fieldsSchema = ref<FieldSchema[]>([]);
  const formError = ref();

  const hasSplitPayment = computed(() => shelterProfile.value?.enable_split_payments);
  const paymentMethodModel = computed(() => fieldsSchema.value.find(({ name }) => name === 'payment_type')?.model);
  const creditCardAmount = computed(
    () => fieldsSchema.value.find(({ name }) => name === 'split_payment')?.model.credit_card_amount
  );
  const isZeroTotal = computed(() => !Number(cartData.value?.total));
  const isBelowMinimumAmount = computed(() => {
    const totalCents = toCents(cartData.value?.total ?? 0);
    const minimumCents = 50; // $0.50 minimum for credit card and split payments
    return totalCents > 0 && totalCents < minimumCents;
  });
  const paymentMethodsOptions = computed(() => {
    return PAYMENT_OPTIONS.filter(({ value }) =>
      value === PAYMENT_METHODS.SPLIT_PAYMENT ? hasSplitPayment.value : true
    ).map((option) => {
      const isOther = option.value === PAYMENT_METHODS.OTHER;
      const isCredit = option.value === PAYMENT_METHODS.CREDIT;
      const isSplitPayment = option.value === PAYMENT_METHODS.SPLIT_PAYMENT;
      const disableCreditCard = isCredit && !shelterProfile.value?.stripe;
      const disabledForZeroTotal = isZeroTotal.value && !isOther;
      const disabledForBelowMinimum = isBelowMinimumAmount.value && (isCredit || isSplitPayment);

      // prettier-ignore
      const message = disableCreditCard ? 'shelterInfo.settings.stripe.notIntegrated'
        : disabledForBelowMinimum ? 'cart.title.paymentMethodRequiresMinimumCreditCard'
        : disabledForZeroTotal && isCredit ? 'cart.title.paymentMethodRequiresMinimumCreditCard'
        : disabledForZeroTotal ? 'cart.title.paymentMethodRequiresAmountGreaterThanZero'
        : '';

      return {
        ...option,
        disabled: disableCreditCard || disabledForZeroTotal || disabledForBelowMinimum,
        disabledMessage: message,
      };
    });
  });

  // NOTE :: Watch the cart response, not only `total`. Switching from credit card to split while
  // the fee is absorbed keeps `total` the same and sets `cc_fee` to 0. A watch on `total` alone
  // never runs again, so the split field kept the old "total minus fee" amount.
  watch(cartData, () => {
    onCartTotalUpdate();
    onCartTotalOrPaymentMethodValidation();
  });
  watch(cashDrawerOptions, () => onCashDrawerOptionsUpdate());
  watch(creditCardAmount, (newValue, oldValue) => onCreditCardAmountUpdate(newValue, oldValue));
  watch(paymentMethodModel, () => onPaymentMethodModelUpdate());
  watch(isBelowMinimumAmount, () => onCartTotalOrPaymentMethodValidation());

  // NOTE :: This is the amount the split must add up to. The api sends `cc_fee` on every card cart.
  // It adds that fee to `total` only when the shelter passes the fee to the adopter. Subtract the
  // fee only in that case. When the shelter absorbs the fee, `total` is already the base amount.
  // Subtracting the fee again showed $96.80 to split on a $100 donation.
  function getAmountToSplit(): number {
    const totalCents = toCents(cartData.value?.total ?? 0);
    const feeIsInTotal = !!shelterProfile.value?.charge_adopter_cc_fee;
    const feeCents = feeIsInTotal ? toCents(cartData.value?.cc_fee ?? 0) : 0;

    return (totalCents - feeCents) / 100;
  }

  function setInitialPaymentType() {
    const currentPaymentType = cartData.value?.payment_type;
    const isCreditOrSplit =
      currentPaymentType === PAYMENT_METHODS.CREDIT || currentPaymentType === PAYMENT_METHODS.SPLIT_PAYMENT;

    if (isZeroTotal.value) {
      fieldsSettings!.payment_type!.model = PAYMENT_METHODS.OTHER;
      return;
    }

    if (isBelowMinimumAmount.value && isCreditOrSplit) {
      fieldsSettings!.payment_type!.model = PAYMENT_METHODS.OTHER;
      return;
    }

    fieldsSettings!.payment_type!.model = currentPaymentType;
  }

  function setForm() {
    if (fieldsSchema.value.length) return;

    setInitialPaymentType();
    fieldsSettings!.payment_type!.edit!.options = paymentMethodsOptions.value;
    fieldsSettings!.cash_drawer!.edit!.options = cashDrawerOptions.value;

    const paidDateField: RawField = {
      label: 'components.forms.labels.paidDate',
      name: 'paid_date',
      required: true,
      type: 'date',
      value: '',
    };
    const rawFieldsToUse = isDonation.value ? [paidDateField, ...rawFields] : rawFields;

    fieldsSchema.value = getDetailsSchema(rawFieldsToUse, fieldsSettings);

    removeHiddenFieldsFromCurrentSchemaModels.value = true;
  }

  function resetFieldSchema() {
    currentSchemaModels.value = new Set();
    fieldsSchema.value = [];
    formError.value = null;
  }

  // NOTE :: Only the server knows which payment type the cart really has. When the save fails the
  // screen still shows the method the user clicked, so it disagrees with the session — cash on
  // screen, credit in the cart. Emptying the field is what forces a pick that gets saved.
  function clearPaymentMethod() {
    const paymentTypeField = fieldsSchema.value.find(({ name }) => name === 'payment_type');
    if (!paymentTypeField) return;

    paymentTypeField.model = '';
  }

  function onCreditCardAmountUpdate(newValue: number, oldValue: number) {
    const noUpdates = oldValue === newValue || oldValue === undefined || newValue === undefined;
    if (noUpdates) return;

    if (!shelterProfile.value?.charge_adopter_cc_fee) return;

    const payload = {
      split_payment_details: {
        credit_card_amount: newValue,
      },
    };

    UpdateCartTotalsService(payload).then((data) => (cartData.value = data));
  }

  function onCartTotalUpdate() {
    fieldsSchema.value.forEach((field) => {
      if (!['split_payment', 'cash_amount_collected'].includes(field.name)) return;

      if (field.name === 'split_payment') {
        field.settings.cartTotal = getAmountToSplit();
        field.settings.cc_fee = Number(cartData.value?.cc_fee ?? 0);
        field.settings.creditCardEnabled = !!shelterProfile.value?.stripe;
      }

      if (field.name === 'cash_amount_collected') {
        field.settings.totalCharge = cartData.value?.total_charge ?? cartData.value?.total ?? 0;
      }
    });
  }

  function onCartTotalOrPaymentMethodValidation() {
    if (!fieldsSchema.value.length) return;

    const paymentTypeField = fieldsSchema.value.find(({ name }) => name === 'payment_type');
    if (!paymentTypeField) return;

    const currentPaymentType = paymentMethodModel.value;
    const isCreditOrSplit =
      currentPaymentType === PAYMENT_METHODS.CREDIT || currentPaymentType === PAYMENT_METHODS.SPLIT_PAYMENT;

    if ((isZeroTotal.value || isBelowMinimumAmount.value) && isCreditOrSplit) {
      paymentTypeField.model = PAYMENT_METHODS.OTHER;
      onPaymentMethodModelUpdate();
    }
  }

  function onPaymentMethodModelUpdate() {
    const { CARD_READER, CHECK, CREDIT, SPLIT_PAYMENT } = PAYMENT_METHODS;

    formError.value = null;

    fieldsSchema.value.forEach((field) => {
      const extraSettingsFields = [
        'stripe',
        'last_four_digits',
        'split_payment',
        'check_number',
        'cash_amount_collected',
      ];
      if (!extraSettingsFields.includes(field.name)) return;

      field.settings.show = false;
      field.settings.required = false;
      field.state.errors = new Set();

      switch (field.name) {
        case 'cash_amount_collected': {
          const isCashField = paymentMethodModel.value === PAYMENT_METHODS.CASH;
          const isFeatureEnabled = !!shelterProfile.value?.enable_change_due_on_cash_payment;
          const showField = isCashField && isFeatureEnabled;

          field.settings.show = showField;
          field.settings.required = false;
          field.settings.totalCharge = cartData.value?.total_charge ?? cartData.value?.total ?? 0;
          field.state.errors = new Set();
          if (!showField) field.model = null;
          break;
        }
        case 'stripe': {
          const isCreditField = paymentMethodModel.value === CREDIT;

          field.settings.creditCardNameRequired = isCreditField;
          field.settings.creditCardNumberRequired = isCreditField;
          field.settings.show = isCreditField;
          field.settings.required = isCreditField;
          field.state.errors = new Set();
          break;
        }
        case 'last_four_digits': {
          const isCardReaderField = paymentMethodModel.value === CARD_READER;

          field.settings.show = isCardReaderField;
          field.state.errors = new Set();
          break;
        }
        case 'split_payment': {
          const isSplitPaymentField = paymentMethodModel.value === SPLIT_PAYMENT;

          field.settings.show = isSplitPaymentField;
          field.settings.required = isSplitPaymentField;
          field.state.errors = new Set();

          if (field.model?.credit_card_amount && isSplitPaymentField) {
            onCreditCardAmountUpdate(field.model.credit_card_amount, field.model.credit_card_amount + 1);
          }

          break;
        }
        case 'check_number': {
          const isCheckNumberField = paymentMethodModel.value === CHECK;

          field.settings.show = isCheckNumberField;
          field.state.errors = new Set();
          break;
        }
      }
    });
  }

  function onCashDrawerOptionsUpdate() {
    // Update cash drawer field options when they become available
    fieldsSchema.value.forEach((field) => {
      if (field.name !== 'cash_drawer') return;

      field.edit!.options = cashDrawerOptions.value;
    });
  }

  return {
    currentSchemaModels,
    fieldsSchema,
    formError,
    paymentMethodModel,

    clearPaymentMethod,
    setForm,
    resetFieldSchema,
  };
}
